import React, { FunctionComponent } from 'react';
import withStyles from 'react-jss';
import { useLocation } from 'react-router-dom';
import { groupBy, propOr } from 'ramda';
import queryString from 'query-string';

import { ClassProp } from 'helpers/types';
import Query from 'scenes/Report/components/Query';
import { ReportParams } from 'scenes/Report/components/ReportBase';

import { GET_TRANSACTIONS } from '../Transactions.queries';
import { renderCharts } from '../Transactions.helper';
import styles from '../Transactions.styles';

type Styles = ClassProp<typeof styles>;

type Props = {
  classes: Styles;
  height?: number;
};

type TransactionSummary = {
  transactionDate: Date;
  cashbackPercentage: number;
  segmentId: string;
  numberOfTransactions: number;
  totalTransactionAmount: number;
  startDate: string;
  endDate: string;
};

type QueryResponse = {
  merchantTransactionSummary?: TransactionSummary[];
};

const header = (x: number) =>
  `Transactions - ${isNaN(x) ? 'Combined' : `${x}%`}`;

const segmentedData = (segmentedTransactions: any) =>
  groupBy(
    (transactions) => propOr('', 'cashbackPercentage', transactions),
    segmentedTransactions,
  );

const batchKey = 'transactions';

const renderReport = ({ classes, height = 450 }: Props) => (
  { merchantTransactionSummary }: QueryResponse,
  params: ReportParams,
): JSX.Element => {
  const transactionsBySegments = segmentedData(merchantTransactionSummary);
  const segments = Object.keys(transactionsBySegments);
  return (
    <>
      {segments.map((cashbackPercentage, idx) =>
        renderCharts(
          params,
          header(parseInt(cashbackPercentage, 10)),
          batchKey,
          parseInt(cashbackPercentage, 10) < 100 ? cashbackPercentage : null,
          'TRANSACTIONS',
          classes,
          height,
          idx,
          transactionsBySegments[cashbackPercentage],
        ),
      )}
    </>
  );
};

const TransactionsByCashbackPercentage: FunctionComponent<Props> = (props) => {
  const location = useLocation();
  const params = queryString.parse(location.search) as ReportParams;
  return (
    <Query
      query={GET_TRANSACTIONS}
      queryKey="result"
      reportName="Transactions"
      batchKey={batchKey}
      variables={{ params }}
      render={renderReport(props)}
    />
  );
};

export default withStyles(styles)(TransactionsByCashbackPercentage);
